import {
    mdiBellBadgeOutline,
    mdiHelpCircleOutline,
    mdiMenu,
    mdiProgressDownload,
    mdiProgressUpload,
    mdiWeb,
} from '@mdi/js';
import Icon from '@mdi/react';
import { Button, MenuItem, Stack, SxProps, Box } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import useBreakpoints from '../../../hooks/useBreakpoint';
import { vars, displayNoneStyle } from '../../../Theme';
import { IAppReduxStore } from '../../../redux/store';
import Logo from '../Logo/Logo';
import HeaderMenuButton from './HeaderMenuButton';
import UserControl from './UserControl/UserControl';
import HeaderProjectInfo from './HeaderProjectInfo';
import LanguageOption from './LanguageOption';
import HelpOption from './HelpOption';
import ProjectRowTag from '../../Views/ProjectSelectionView/ProjectList/ProjectRowTag';
import i18n from '../../../localization/i18n';
import UploadProgress from './UploadProgress';
import {
    uploadActiveSelector,
    uploadedFilesSelector,
} from '../../../redux/import/sliceImportFileUpload';
import { setIsHidden as setUploadDialogHidden } from '../../../redux/import/sliceImportDialog';
import IconLoadingWrapper from '../Icons/IconLoadingWrapper';

const headerButtonSx: SxProps = {
    minWidth: '24px',
    justifyContent: 'left',
    textTransform: 'none',
    color: vars.font,
};

/**
 * Component for a button that is shown in the header.
 */
const HeaderButton = (props: {
    text?: string;
    title?: string;
    icon?: string;
    onClick?: React.MouseEventHandler;
    sx?: SxProps;
}) => {
    /** Deconstruct props. */
    const { icon, title, text, onClick, sx } = props;

    return (
        <Button title={title} onClick={onClick} sx={{ ...headerButtonSx, ...sx }}>
            {icon && <Icon path={icon} size={1} />}
            {text}
        </Button>
    );
};

/**
 * Main application header.
 */
export default () => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'header' });

    /** Determine screen size. */
    const { isDesktop, isTablet, isMobile } = useBreakpoints();

    const dispatch = useDispatch();

    /** Get relevant values from Redux store. */
    const metadata = useSelector((store: IAppReduxStore) => store.projectMetadata.data);
    const projects = useSelector((store: IAppReduxStore) => store.userProjects.data);

    const importFiles = useSelector(
        (_store: IAppReduxStore) => _store.projectFileUpload.fileModels,
    );
    const uploadedFiles = useSelector(uploadedFilesSelector);
    const uploadActive = useSelector(uploadActiveSelector);

    const downloadActive = useSelector(
        (store: IAppReduxStore) =>
            store.projectFileDownload.loadingSingleDocument ||
            store.projectFileDownload.downloadDocuments.loading ||
            store.projectFileDownload.downloadZip.loading,
    );

    const exportActive = useSelector(
        (store: IAppReduxStore) => store.projectAdvancedExportDialog.loading,
    );

    /** Get the replicaId from the react router. */
    const { replicaId } = useParams();

    /** State slice that controls the open state of the header's menu on mobile devices. */
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    /** State slice that controls the open state of the Project Selection dropdown. */
    const [open, setOpen] = useState<boolean>(false);

    const module = useMemo(
        () => projects.find(x => x.replicaId === replicaId)?.module,
        [projects, replicaId],
    );

    /** Header element styles. */
    const headerSx: SxProps = {
        bgcolor: vars.logoBlue,
        justifyContent: 'left',
        flexDirection: 'row',
        alignItems: 'center',
        position: 'relative',
        textAlign: 'left',
        color: vars.white,
        width: '100%',
    };

    /** Controls wrapper element styles. */
    const controlsSx: SxProps = {
        justifyContent: 'right',
        flexDirection: 'row',
        alignItems: 'center',
    };

    /** Styles for the Box wrapping the Project tag. */
    const tagBoxSx: SxProps = {
        borderRadius: '10px',
        ml: 3,
        mr: -1 /** Removing the right margin coming from ProjectRowTag to adjust the position. */,
    };

    /**
     * Toggles the visibility of the mobile menu.
     */
    const handleToggleMobileMenu = useCallback(
        () => setMobileMenuOpen(!mobileMenuOpen),
        [mobileMenuOpen],
    );

    /**
     * Function to toggle Autocomplete open state.
     */
    const toggleProjectDropdown = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        setOpen(!open);
    };

    /**
     * Function to handle click outside Autocomplete.
     */
    const closeProjectDropdown = (e: MouseEvent | TouchEvent) => {
        e.preventDefault();
        setOpen(false);
    };

    /** Desktop and tablet view. */
    if (isDesktop || isTablet) {
        return (
            <Stack sx={{ ...headerSx }}>
                <Logo boxSx={{ pl: 3 }} />

                {!isTablet && module && (
                    <Box sx={tagBoxSx}>
                        <ProjectRowTag
                            moduleAbbreviation={module.moduleAbbreviation}
                            tagColor={module.tagColor}
                            tooltipTitle={module.translations[i18n.language]}
                            key={replicaId}
                        />
                    </Box>
                )}

                {metadata !== null && (
                    <HeaderProjectInfo
                        style={{ minWidth: '320px' }}
                        open={open}
                        toggleDropdownOnclick={toggleProjectDropdown}
                        closeDropdown={closeProjectDropdown}
                    />
                )}
                <Stack sx={{ flexGrow: 4 }}></Stack>

                <Stack sx={{ ...controlsSx }}>
                    {/** Notifications button currently not displayed. */}
                    <HeaderMenuButton
                        sx={displayNoneStyle}
                        icon={mdiBellBadgeOutline}
                        title={t('notifications')}
                        text={isTablet ? '' : t('notifications')}
                        color={vars.white}
                    >
                        <MenuItem>{t('comingSoon')}</MenuItem>
                    </HeaderMenuButton>

                    {(exportActive || downloadActive || uploadActive) && (
                        <HeaderMenuButton
                            icon={
                                <IconLoadingWrapper
                                    icon={uploadActive ? mdiProgressUpload : mdiProgressDownload}
                                />
                            }
                            title={'Upload'}
                            text={
                                isTablet
                                    ? ''
                                    : uploadActive
                                    ? 'Upload'
                                    : exportActive
                                    ? 'Export'
                                    : 'Download'
                            }
                            color={vars.white}
                            sx={{ gap: '8px' }}
                        >
                            <UploadProgress
                                progress={{
                                    current: uploadedFiles.length,
                                    total: importFiles.length,
                                }}
                                onMaximizeClick={() => dispatch(setUploadDialogHidden(false))}
                            />
                        </HeaderMenuButton>
                    )}

                    <HeaderMenuButton
                        icon={mdiHelpCircleOutline}
                        title={t('help')}
                        text={isTablet ? '' : t('help')}
                        color={vars.white}
                    >
                        <HelpOption />
                    </HeaderMenuButton>

                    <HeaderMenuButton
                        icon={mdiWeb}
                        text={i18n.language}
                        color={vars.white}
                        sx={{ textTransform: 'uppercase' }}
                    >
                        {[...i18n.languages]
                            .sort((a, b) => t(a).localeCompare(t(b)))
                            .map(x => (
                                <LanguageOption key={x} lang={x} />
                            ))}
                    </HeaderMenuButton>

                    <UserControl />
                </Stack>
            </Stack>
        );
    }

    /** Menu element styles. */
    const menuSx: SxProps = {
        position: 'absolute',
        bgcolor: vars.white,
        zIndex: 1220,
        bottom: 0,
        right: 0,
        top: 56,
        left: 0,
        p: 1,

        button: {
            textAlign: 'left',
            borderRadius: 1,
            width: '100%',
            fontSize: 16,
            height: 56,
            px: 3,

            '&.active': {
                bgcolor: vars.bgGreen,
            },
        },
    };

    return (
        <Stack direction='row'>
            <Stack sx={{ ...headerSx }}>
                <HeaderButton
                    title={t('menu')}
                    onClick={handleToggleMobileMenu}
                    icon={mdiMenu}
                    sx={{ bgColor: 'transparent', p: 2, color: vars.white, border: 0 }}
                />

                <Logo
                    boxSx={{
                        display: 'flex',
                        flex: 1,
                        justifyContent: 'left',
                        ml: isTablet ? 3 : 0,
                    }}
                />

                <Stack sx={{ ...controlsSx }}>
                    {isTablet && (
                        <Stack>
                            {metadata !== null && (
                                <HeaderProjectInfo
                                    style={{ minWidth: '320px' }}
                                    open={open}
                                    toggleDropdownOnclick={toggleProjectDropdown}
                                    closeDropdown={closeProjectDropdown}
                                />
                            )}
                        </Stack>
                    )}

                    <UserControl />
                </Stack>
            </Stack>

            {isMobile && mobileMenuOpen && (
                <Stack sx={{ ...menuSx }}>
                    {metadata !== null && (
                        <HeaderProjectInfo
                            style={{
                                width: 'auto',
                                marginLeft: 'unset',
                            }}
                            open={open}
                            toggleDropdownOnclick={toggleProjectDropdown}
                            closeDropdown={closeProjectDropdown}
                        />
                    )}
                    <HeaderButton text={t('projects')} />
                    <HeaderButton text={t('administration')} />
                    <HeaderButton text={t('profile')} sx={{ bgcolor: vars.bgGreen }} />

                    <Stack sx={{ button: { pl: 4.5 } }}>
                        <HeaderButton text={t('profile')} />
                        <HeaderButton text={t('passwordSecurity')} />
                        <HeaderButton text={t('accountSelection')} />
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
};
